import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SITUACAO } from "@/lib/meta";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Search, Boxes, AlertOctagon, X, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

function SituacaoBadge({ s }) {
  const m = SITUACAO[s] || SITUACAO.NORMAL;
  return <span className={`inline-flex rounded-sm border px-2 py-0.5 text-[10px] font-bold uppercase font-mono ${m.cls}`}>{m.label}</span>;
}

const DEC_DESTINOS = [
  ["RETORNO", "Retornar ao estoque", "Volta para o disponível."],
  ["MANUTENCAO", "Manutenção / Recuperação", "Mantém bloqueado, marcado para recuperação."],
  ["DESCARTE", "Descarte / Perda", "Baixa definitiva do estoque (exige confirmação)."],
  ["OUTRO", "Outro destino", "Saída do balde com motivo registrado."],
];

export default function Estoque() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [units, setUnits] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [unit, setUnit] = useState(searchParams.get("unit_id") || "TODAS");
  const [somenteCriticos, setSomenteCriticos] = useState(searchParams.get("situacao") === "critico");
  const [busca, setBusca] = useState("");
  const [dec, setDec] = useState(null); // {row, origem, destino, motivo, quantidade, confirmar}
  const [busy, setBusy] = useState(false);
  const canDecide = ["SUPER_ADMIN", "MASTER", "GESTOR", "SUPERVISOR"].includes(user.role);

  const reload = () => {
    const q = unit !== "TODAS" ? `?unit_id=${unit}` : "";
    setRows(null);
    api.get(`/estoque${q}`).then((r) => setRows(r.data));
  };
  const openDec = (row) => {
    const origem = row.bloqueado > 0 ? "BLOQUEADO" : "QUARENTENA";
    setDec({ row, origem, destino: "RETORNO", motivo: "", quantidade: row[origem === "BLOQUEADO" ? "bloqueado" : "quarentena"], confirmar: false });
  };
  const submitDec = async () => {
    if (!dec.motivo.trim()) return toast.error("Informe o motivo da decisão");
    if (dec.destino === "DESCARTE" && !dec.confirmar) return toast.error("Confirme o descarte (baixa definitiva)");
    setBusy(true);
    try {
      await api.post("/estoque/avaria/decisao", {
        unit_id: dec.row.unit_id, sku_id: dec.row.sku_id, quantidade: Number(dec.quantidade) || 0,
        origem: dec.origem, destino: dec.destino, motivo: dec.motivo, confirmar_descarte: dec.confirmar,
      });
      toast.success("Decisão registrada");
      setDec(null); reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao decidir"); }
    finally { setBusy(false); }
  };

  useEffect(() => { api.get("/units").then((r) => setUnits(r.data)); }, []);
  useEffect(() => {
    setUnit(searchParams.get("unit_id") || "TODAS");
    setSomenteCriticos(searchParams.get("situacao") === "critico");
  }, [searchParams]);
  useEffect(() => {
    const q = unit !== "TODAS" ? `?unit_id=${unit}` : "";
    setRows(null);
    api.get(`/estoque${q}`).then((r) => setRows(r.data));
  }, [unit]);

  const filtered = useMemo(() => (rows || []).filter((r) => {
    const b = busca.toLowerCase();
    const matchBusca = !b || r.produto.nome.toLowerCase().includes(b) || r.produto.sku.toLowerCase().includes(b) || r.unidade_nome.toLowerCase().includes(b);
    const matchSit = !somenteCriticos || r.situacao !== "NORMAL";
    return matchBusca && matchSit;
  }), [rows, busca, somenteCriticos]);

  const limparCriticos = () => { setSomenteCriticos(false); setSearchParams({}, { replace: true }); };

  return (
    <div className="space-y-5" data-testid="estoque-page">
      <div>
        <div className="text-xs text-primary uppercase tracking-[0.18em] font-bold">Ledger & Saldos</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold uppercase">Estoque</h1>
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto / SKU / unidade" className="pl-9" data-testid="estoque-busca" />
        </div>
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="w-56" data-testid="estoque-unidade"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="TODAS">Todas as unidades</SelectItem>
            {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.codigo} — {u.nome}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {somenteCriticos && (
        <div className="flex items-center gap-2 flex-wrap rounded-md border border-amber-600/50 bg-amber-950/30 px-3 py-2 text-sm" data-testid="estoque-drill-banner">
          <AlertOctagon className="w-4 h-4 text-amber-300 shrink-0" />
          <span className="text-xs uppercase tracking-wide text-amber-300 font-bold">Mostrando apenas SKUs em situação crítica (baixo / sem estoque)</span>
          <button onClick={limparCriticos} className="ml-auto inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="estoque-drill-limpar">
            <X className="w-3.5 h-3.5" /> Ver todos
          </button>
        </div>
      )}

      {!rows ? (
        <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <Card className="bg-card/70 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-3">Produto</th>
                <th className="text-left p-3">Unidade</th>
                <th className="text-right p-3">Disponível</th>
                <th className="text-right p-3">Reservado</th>
                <th className="text-right p-3">Em Trânsito</th>
                <th className="text-right p-3">Bloqueado</th>
                <th className="text-right p-3">Quarentena</th>
                <th className="text-right p-3">Mínimo</th>
                <th className="text-center p-3">Situação</th>
                <th className="text-center p-3">Avaria</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={`${r.unit_id}-${r.sku_id}`} className="border-b border-border/60 hover:bg-secondary/30" data-testid={`estoque-row-${r.produto.sku}-${r.unidade_codigo}`}>
                  <td className="p-3">
                    <div className="font-medium">{r.produto.nome}</div>
                    <div className="text-[10px] font-mono text-muted-foreground">{r.produto.sku} · {r.produto.marca}</div>
                  </td>
                  <td className="p-3"><span className="font-mono">{r.unidade_codigo}</span> <span className="text-xs text-muted-foreground">{r.unidade_tipo}</span></td>
                  <td className="p-3 text-right font-mono font-bold">{r.disponivel}</td>
                  <td className="p-3 text-right font-mono text-amber-300">{r.reservado}</td>
                  <td className="p-3 text-right font-mono text-sky-300">{r.em_transito}</td>
                  <td className={`p-3 text-right font-mono ${r.bloqueado > 0 ? "text-rose-300 font-bold" : "text-muted-foreground/40"}`} data-testid={`estoque-bloqueado-${r.produto.sku}-${r.unidade_codigo}`}>{r.bloqueado || 0}</td>
                  <td className={`p-3 text-right font-mono ${r.quarentena > 0 ? "text-yellow-300 font-bold" : "text-muted-foreground/40"}`} data-testid={`estoque-quarentena-${r.produto.sku}-${r.unidade_codigo}`}>{r.quarentena || 0}</td>
                  <td className="p-3 text-right font-mono text-muted-foreground">{r.minimo}</td>
                  <td className="p-3 text-center"><SituacaoBadge s={r.situacao} /></td>
                  <td className="p-3 text-center">
                    {canDecide && ((r.bloqueado || 0) > 0 || (r.quarentena || 0) > 0) ? (
                      <Button size="sm" variant="outline" className="text-rose-300 border-rose-500/40 h-7" onClick={() => openDec(r)} data-testid={`btn-decidir-avaria-${r.produto.sku}-${r.unidade_codigo}`}>
                        <ShieldAlert className="w-3.5 h-3.5" /> Tratar
                      </Button>
                    ) : <span className="text-muted-foreground/30">—</span>}
                  </td>
                </tr>
              ))}
              {!filtered.length && <tr><td colSpan={10} className="p-10 text-center text-muted-foreground">Sem saldos.</td></tr>}
            </tbody>
          </table>
        </Card>
      )}

      <Dialog open={!!dec} onOpenChange={(v) => !v && setDec(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-300" /> Decisão de avaria</DialogTitle>
            <DialogDescription>{dec?.row?.produto?.nome} · {dec?.row?.unidade_codigo}</DialogDescription>
          </DialogHeader>
          {dec && (
            <div className="space-y-3">
              {dec.row.bloqueado > 0 && dec.row.quarentena > 0 && (
                <div>
                  <Label className="text-xs uppercase">Origem</Label>
                  <div className="flex gap-2 mt-1">
                    {["BLOQUEADO", "QUARENTENA"].map((o) => (
                      <button key={o} type="button" onClick={() => setDec((d) => ({ ...d, origem: o, quantidade: o === "BLOQUEADO" ? d.row.bloqueado : d.row.quarentena }))} className={`flex-1 text-xs font-semibold px-3 py-2 rounded-md border ${dec.origem === o ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground"}`} data-testid={`dec-origem-${o}`}>{o === "BLOQUEADO" ? "Bloqueado" : "Quarentena"}</button>
                    ))}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-muted-foreground">Origem: <b className="text-foreground">{dec.origem === "BLOQUEADO" ? "Bloqueado/Avariado" : "Quarentena"}</b> · disponível no balde: {dec.origem === "BLOQUEADO" ? dec.row.bloqueado : dec.row.quarentena}</div>
              <div>
                <Label className="text-xs uppercase">Destino</Label>
                <div className="space-y-1.5 mt-1">
                  {DEC_DESTINOS.map(([v, l, h]) => (
                    <label key={v} className={`flex items-start gap-2 p-2 rounded-md border cursor-pointer ${dec.destino === v ? "border-primary bg-primary/10" : "border-border"}`} data-testid={`dec-destino-${v}`}>
                      <input type="radio" name="dec-destino" checked={dec.destino === v} onChange={() => setDec((d) => ({ ...d, destino: v, confirmar: false }))} className="mt-1 accent-primary" />
                      <span><span className="text-sm font-semibold block">{l}</span><span className="text-[11px] text-muted-foreground">{h}</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase">Quantidade</Label>
                <Input type="number" min={1} value={dec.quantidade} onChange={(e) => setDec((d) => ({ ...d, quantidade: e.target.value }))} className="h-9" data-testid="dec-qtd" />
              </div>
              <div>
                <Label className="text-xs uppercase">Motivo (obrigatório)</Label>
                <Textarea value={dec.motivo} onChange={(e) => setDec((d) => ({ ...d, motivo: e.target.value }))} data-testid="dec-motivo" />
              </div>
              {dec.destino === "DESCARTE" && (
                <label className="flex items-center gap-2 text-xs text-rose-300 cursor-pointer" data-testid="dec-confirmar">
                  <input type="checkbox" checked={dec.confirmar} onChange={(e) => setDec((d) => ({ ...d, confirmar: e.target.checked }))} className="accent-rose-400 w-4 h-4" />
                  Confirmo o DESCARTE/PERDA — baixa definitiva de estoque.
                </label>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDec(null)}>Cancelar</Button>
            <Button onClick={submitDec} disabled={busy} data-testid="dec-confirmar-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Registrar decisão"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
